import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { stats, stepWindows } from '@/constants/content'
import { getControl, live } from '@/lib/drone/control'

import {
    clamp,
    easeOutCubic,
    mixHex,
    range,
    smoothstep,
    stepped
} from '@/lib/motion/math'

import {
    emptyMetrics,
    enterProgress,
    isNear,
    measureSection,
    pinProgress,
    relativeRect,
    useEngine
} from '@/lib/motion/section'

import {
    Bracket,
    createStyleCache,
    scrambleText
} from '@/components/landing/primitives'

const round = (value: number): number => Number(value.toFixed(2))

const ARC_RADIUS = 100
const ARC_LENGTH = Math.PI * ARC_RADIUS

const TICKS = Array.from({ length: 37 }, (_, index) => {
    const angle = (index * 5 * Math.PI) / 180
    const major = index % 6 === 0
    const inner = ARC_RADIUS - (major ? 9 : 4.5)

    return {
        major,
        x1: round(-Math.cos(angle) * ARC_RADIUS),
        y1: round(Math.sin(angle) * ARC_RADIUS),
        x2: round(-Math.cos(angle) * inner),
        y2: round(Math.sin(angle) * inner)
    }
})

const MARKERS = [30, 90, 150].map((degrees) => {
    const angle = (degrees * Math.PI) / 180
    const dx = -Math.cos(angle)
    const dy = Math.sin(angle)
    const px = -dy
    const py = dx
    const tip = ARC_RADIUS + 2.5
    const base = ARC_RADIUS + 9

    return [
        `${round(dx * tip)},${round(dy * tip)}`,
        `${round(dx * base + px * 3.4)},${round(dy * base + py * 3.4)}`,
        `${round(dx * base - px * 3.4)},${round(dy * base - py * 3.4)}`
    ].join(' ')
})

const RING_DOTS = Array.from({ length: 8 }, (_, index) => {
    const angle = (index * Math.PI) / 4

    return { cx: round(16 + Math.cos(angle) * 9), cy: round(16 + Math.sin(angle) * 9) }
})

const GRID_DOTS = [10, 16, 22].flatMap(y => [10, 16, 22].map(x => ({ cx: x, cy: y })))

const DialIcon: FC<{ index: number }> = ({ index }): ReactNode => {
    if (index === 0) {
        return <circle cx='16' cy='16' r='3.2' />
    }

    if (index === 1) {
        return (
            <>
                {GRID_DOTS.map(dot => <circle key={`${dot.cx}-${dot.cy}`} cx={dot.cx} cy={dot.cy} r='1.6' />)}
            </>
        )
    }

    return (
        <>
            <circle cx='16' cy='16' r='1.8' />
            {RING_DOTS.map(dot => <circle key={`${dot.cx}-${dot.cy}`} cx={dot.cx} cy={dot.cy} r='1.35' />)}
        </>
    )
}

const NumbersSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const arcRef = useRef<SVGSVGElement>(null)
    const arcPathRef = useRef<SVGPathElement>(null)
    const railRef = useRef<HTMLDivElement>(null)
    const dialRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLSpanElement>(null)
    const iconRefs = useRef<Array<SVGGElement | null>>([])
    const statRefs = useRef<Array<HTMLDivElement | null>>([])
    const countRefs = useRef<Array<HTMLSpanElement | null>>([])
    const headingRef = useRef<HTMLDivElement>(null)

    useEngine(() => {
        const set = createStyleCache()
        let metrics = emptyMetrics()
        let anchorX = 0
        let anchorY = 0
        let followX = 0
        let followY = 0
        let active = -1
        let cancelScramble: () => void = () => undefined
        const lastCounts = [-1, -1, -1]

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current, frameRef.current)

                const anchor = frameRef.current?.querySelector<HTMLElement>('[data-anchor="numbers-center"]')

                if (anchor && frameRef.current) {
                    const rect = relativeRect(anchor, frameRef.current)

                    anchorX = rect.cx
                    anchorY = rect.cy
                }
            },
            frame: (state) => {
                if (!isNear(metrics, state.scroll, state.vh)) return

                const enter = enterProgress(metrics, state.scroll, state.vh)
                const p = pinProgress(metrics, state.scroll)
                const focus = 0.5 + stepped(p, stepWindows.numbers)
                const index = Math.min(2, Math.max(0, Math.round(focus - 0.5)))
                const draw = smoothstep(0.35, 1, enter)

                set(arcPathRef.current, 'stroke-dashoffset', (ARC_LENGTH * (1 - draw)).toFixed(2))
                const pinEnd = metrics.top + metrics.height - metrics.frame
                const frameTop = state.scroll < metrics.top ? metrics.top - state.scroll : state.scroll > pinEnd ? pinEnd - state.scroll : 0
                const following = getControl().mode === 'manual'
                const followFactor = 1 - Math.exp(-4 * state.dt)

                followX += ((following ? live.telemetry.x - anchorX : 0) - followX) * followFactor
                followY += ((following ? live.telemetry.y - (frameTop + anchorY) : 0) - followY) * followFactor

                set(arcRef.current, 'transform', `translate(calc(-50% + ${followX.toFixed(1)}px), calc(-50% + ${followY.toFixed(1)}px)) rotate(${((focus - 1.5) * 9).toFixed(3)}deg)`)
                set(arcRef.current, 'opacity', smoothstep(0.2, 0.7, enter).toFixed(3))
                set(railRef.current, 'transform', `scaleX(${smoothstep(0.4, 1, enter).toFixed(4)})`)
                set(dialRef.current, 'transform', `translate(-50%, -50%) scale(${(0.6 + 0.4 * smoothstep(0.55, 1, enter)).toFixed(3)})`)
                set(dialRef.current, 'opacity', smoothstep(0.55, 0.9, enter).toFixed(3))
                set(headingRef.current, 'opacity', smoothstep(0.5, 1, enter).toFixed(3))

                const counting = easeOutCubic(range(0.45, 1, enter))

                stats.forEach((stat, statIndex) => {
                    const strength = clamp(1 - (Math.abs(focus - (statIndex + 0.5)) - 0.12) / 0.55)
                    const element = statRefs.current[statIndex]

                    set(element, 'color', mixHex('#c6cdd0', '#1d2a31', strength))
                    set(element, 'filter', `blur(${((1 - strength) * 1.6).toFixed(2)}px)`)

                    const value = Math.round(stat.value * counting)

                    if (value !== lastCounts[statIndex]) {
                        lastCounts[statIndex] = value

                        const target = countRefs.current[statIndex]

                        if (target) target.textContent = value.toLocaleString('en-US')
                    }
                })

                if (index !== active) {
                    if (active !== -1 && enter > 0.85) live.lidarPulseAt = state.time

                    active = index
                    cancelScramble()
                    cancelScramble = scrambleText(labelRef.current, stats[index].dial.toUpperCase(), 480)

                    iconRefs.current.forEach((icon, iconIndex) => {
                        if (!icon) return

                        icon.style.opacity = iconIndex === index ? '1' : '0'
                        icon.style.transform = iconIndex === index ? 'scale(1)' : 'scale(0.6)'
                    })
                }
            },
            cleanup: () => cancelScramble()
        }
    })

    return (
        <section id='numbers' ref={sectionRef} aria-labelledby='numbers-title' className='relative h-[320vh] md:h-[340vh]'>
            <div ref={frameRef} data-frame='' className='sticky top-0 h-svh overflow-hidden'>
                <div ref={headingRef} className='absolute left-5 top-[84px] flex items-center gap-4 text-haze md:left-10'>
                    <h2 id='numbers-title' className='label-mono'>
                        [ By the numbers ]
                    </h2>
                    <span aria-hidden='true' className='hidden h-px w-12 bg-pale md:block' />
                    <span className='label-mono hidden md:inline'>Tested at 100 m above ground</span>
                </div>

                <div aria-hidden='true' data-anchor='numbers-center' className='absolute left-1/2 top-[22%] size-px md:top-[30%]' />

                <svg
                    ref={arcRef}
                    aria-hidden='true'
                    viewBox='-112 -112 224 224'
                    className='absolute left-1/2 top-[22%] h-auto w-[min(80vw,56vh)] overflow-visible md:top-[30%] md:w-[min(36vw,56vh)]'
                    style={{ transform: 'translate(-50%, -50%)' }}
                >
                    <path
                        ref={arcPathRef}
                        d={`M -${ARC_RADIUS} 0 A ${ARC_RADIUS} ${ARC_RADIUS} 0 0 0 ${ARC_RADIUS} 0`}
                        fill='none'
                        stroke='#1d2a31'
                        strokeOpacity='0.55'
                        strokeWidth='0.6'
                        strokeDasharray={ARC_LENGTH}
                        strokeDashoffset={ARC_LENGTH}
                        vectorEffect='non-scaling-stroke'
                    />
                    <path d='M -86 0 A 86 86 0 0 0 86 0' fill='none' stroke='#9aa3a8' strokeWidth='0.5' strokeDasharray='1.5 4' vectorEffect='non-scaling-stroke' />

                    {TICKS.map((tick, index) => (
                        <line
                            key={index}
                            x1={tick.x1}
                            y1={tick.y1}
                            x2={tick.x2}
                            y2={tick.y2}
                            stroke={tick.major ? '#1d2a31' : '#9aa3a8'}
                            strokeWidth={tick.major ? 0.8 : 0.5}
                            vectorEffect='non-scaling-stroke'
                        />
                    ))}

                    {MARKERS.map(points => (
                        <polygon key={points} points={points} fill='#1d2a31' />
                    ))}

                    <circle cx='0' cy='0' r='2' fill='none' stroke='#8fe3f0' strokeWidth='0.8' vectorEffect='non-scaling-stroke' />
                </svg>

                <div className='absolute inset-x-0 bottom-0 top-[50%] md:top-[66%]'>
                    <div ref={railRef} aria-hidden='true' className='absolute inset-x-3 top-0 h-px origin-center bg-line md:inset-x-5' />

                    <div className='absolute left-1/2 top-0 flex -translate-x-1/2 -translate-y-[calc(100%_+_42px)] justify-center text-ink'>
                        <span className='label-mono whitespace-nowrap'>
                            <span aria-hidden='true'>[ </span>
                            <span ref={labelRef} aria-live='polite'>{stats[0].dial.toUpperCase()}</span>
                            <span aria-hidden='true'> ]</span>
                        </span>
                    </div>

                    <div
                        ref={dialRef}
                        aria-hidden='true'
                        className='absolute left-1/2 top-0 z-10 flex size-14 items-center justify-center rounded-full border border-line bg-background shadow-[0_8px_24px_-12px_rgba(29,42,49,0.25)] md:size-16'
                        style={{ transform: 'translate(-50%, -50%)' }}
                    >
                        <svg viewBox='0 0 32 32' className='size-8 fill-ink'>
                            {stats.map((stat, index) => (
                                <g
                                    key={stat.dial}
                                    ref={(element) => { iconRefs.current[index] = element }}
                                    style={{
                                        opacity: index === 0 ? 1 : 0,
                                        transform: index === 0 ? 'scale(1)' : 'scale(0.6)',
                                        transformOrigin: '16px 16px',
                                        transition: 'opacity 0.45s ease, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)'
                                    }}
                                >
                                    <DialIcon index={index} />
                                </g>
                            ))}
                        </svg>
                    </div>

                    <div className='grid h-full grid-rows-3 pt-9 md:grid-cols-3 md:grid-rows-1 md:pt-0'>
                        {stats.map((stat, index) => (
                            <div
                                key={stat.label}
                                ref={(element) => { statRefs.current[index] = element }}
                                className={cn(
                                    'flex flex-col justify-center gap-2 border-line px-5 md:gap-4 md:px-10',
                                    index > 0 && 'border-t md:border-l md:border-t-0'
                                )}
                                style={{ color: index === 0 ? '#1d2a31' : '#c6cdd0' }}
                            >
                                <p className='flex items-baseline gap-3'>
                                    <span className='text-[clamp(48px,7vw,124px)] font-light leading-[0.9] tracking-[-0.06em] tabular-nums'>
                                        {stat.prefix}
                                        <span ref={(element) => { countRefs.current[index] = element }}>
                                            {stat.value.toLocaleString('en-US')}
                                        </span>
                                    </span>

                                    <span className='text-[clamp(16px,1.5vw,24px)] tracking-[-0.02em]'>
                                        {stat.unit}
                                    </span>
                                </p>

                                <Bracket>{stat.label}</Bracket>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default NumbersSection
